"use client";

import {useEffect} from "react";
import {usePathname} from "next/navigation";

const COOKIE_POLICY_ID = "ot-sdk-cookie-policy";
const RETRY_DELAY_MS = 400;
const MAX_RETRIES = 10;
const CLEAR_DUP_DELAY_MS = 1200;

// The OneTrust overlay (#onetrust-consent-sdk — banner + floating settings
// icon) is appended directly to <body>, outside the React tree, so it already
// survives soft navigation. Never remove it to "refresh" it: once that
// container is gone, neither Init() nor LoadBanner() rebuilds it, and the
// banner and icon stay missing until a full page reload.
//
// The only thing a soft navigation does leave stale is the cookie list on the
// cookie policy page, which the SDK renders once on its own initial load.
export default function OneTrustSpa() {
    const pathname = usePathname();

    useEffect(() => {
        // #ot-sdk-cookie-policy comes from the CMS rich text, so it is already
        // in the DOM here. Any other page has nothing to sync.
        if (!document.getElementById(COOKIE_POLICY_ID)) {
            return;
        }

        const timeoutIds: number[] = [];

        const schedule = (callback: () => void, delay: number) => {
            const timeoutId = window.setTimeout(callback, delay);
            timeoutIds.push(timeoutId);
        };

        // The SDK arrives via GTM, so it may not be ready on the first pass.
        const initializeCookiePolicy = (retriesLeft: number) => {
            if (typeof window.OneTrust?.initializeCookiePolicyHtml !== "function") {
                if (retriesLeft > 0) {
                    schedule(() => initializeCookiePolicy(retriesLeft - 1), RETRY_DELAY_MS);
                }

                return;
            }

            window.OneTrust.initializeCookiePolicyHtml(true);

            if (typeof window.clearDup === "function") {
                schedule(() => window.clearDup?.(), CLEAR_DUP_DELAY_MS);
            }
        };

        initializeCookiePolicy(MAX_RETRIES);

        return () => {
            timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
        };
    }, [pathname]);

    return null;
}
